import axios from 'axios';

// Get API base URL from environment variable or use default
const getApiBaseUrl = () => {
  // In production build, use REACT_APP_API_URL from .env
  // In development, fallback to local network
  return process.env.REACT_APP_API_URL || 'http://localhost:8000';
};

const API_BASE_URL = getApiBaseUrl();

// Configuration - now dynamically using environment variable
const API_CONFIG = {
  baseUrl: API_BASE_URL,
  userUrl: `${API_BASE_URL}/users/`,
  workOrderUrl: `${API_BASE_URL}/WorkOrder/`,
  customerUrl: `${API_BASE_URL}/customers/`,
  jobsUrl: `${API_BASE_URL}/jobs/`,
  quotesUrl: `${API_BASE_URL}/quotes/`,
  quickbooksUrl: `${API_BASE_URL}/quickbooks/`,
  notificationsUrl: `${API_BASE_URL}/notifications/`,
  wheelsUrl: `${API_BASE_URL}/wheels/`,
  schedulingUrl: `${API_BASE_URL}/scheduling/`,
  crmUrl: `${API_BASE_URL}/crm/`,
  inventoryUrl: `${API_BASE_URL}/inventory/`,
  portalUrl: `${API_BASE_URL}/commercial-portal/`,
};

// Get current API configuration
const getCurrentConfig = () => {
  return API_CONFIG;
};


// Get current API URLs
const currentConfig = getCurrentConfig();
const USER_API_URL = currentConfig.userUrl;
const WORKORDER_BASE_URL = currentConfig.workOrderUrl;
const CUST_BASE_URL = currentConfig.customerUrl;
const JOBS_BASE_URL = currentConfig.jobsUrl;
const QUOTES_BASE_URL = currentConfig.quotesUrl;
const QUICKBOOKS_BASE_URL = currentConfig.quickbooksUrl;
const NOTIFICATIONS_BASE_URL = currentConfig.notificationsUrl;
const WHEELS_BASE_URL = currentConfig.wheelsUrl;
const SCHEDULING_BASE_URL = currentConfig.schedulingUrl;
const CRM_BASE_URL = currentConfig.crmUrl;
const INVENTORY_BASE_URL = currentConfig.inventoryUrl;
const PORTAL_BASE_URL = currentConfig.portalUrl;

const getAuthToken = () => {
    const token = localStorage.getItem('authToken');
    return token;
  };
  
  const authHeader = () => {
    const token = getAuthToken();
    if (token) {
      return { Authorization: `Token ${token}` };
    }
    return {};
  };
  
  export const register = async (username, email, password) => {
    const headers = authHeader();
    const response = await axios.post(USER_API_URL + 'register/', {
      username,
      email,
      password,
    }, { headers });
    
    const token = response.data.token;
    localStorage.setItem('authToken', token);
    return response.data;
  };
  
  export const login = async (username, password) => {
    const response = await axios.post(USER_API_URL + 'login/', {
      username,
      password,
    });
    const token = response.data.token;
    localStorage.setItem('authToken', token);
    return response.data;
  };

  export const setAuthToken = (token) => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Token ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  };
  
  export const getUserData = () => {
    const headers = authHeader();
    return axios.get(USER_API_URL + 'user_data/', { headers });
  };



//WorkOrder APIS

// `extra`: optional list params such as ordering, created_from, created_to (empty values are left out).
export const fetchWorkOrders = async (service, date, search, status, archived = false, page = 1, pageSize = 50, billing = 'all', extra = {}) => {
  try {
    const params = new URLSearchParams({
      ...(service && { service }),
      ...(date && { date }),
      ...(search && { search }),
      ...(status && status !== 'all' && { status }),
      ...(billing && billing !== 'all' && { billing }),
      ...Object.fromEntries(Object.entries(extra).filter(([, value]) => value)),
      archived: archived ? 'true' : 'false',
      page: page,
      page_size: pageSize
    });
    const url = `${WORKORDER_BASE_URL}list/?${params.toString()}`;
    const response = await fetch(url, {
      headers: authHeader()
    });
    const workOrders = await response.json();
    return workOrders;
  } catch (error) {
    console.error('Error fetching work orders:', error);
    throw error;
  }
};

export const fetchWorkOrdersByCustomer = async (service, date, customerId, status, archived = false, page = 1, pageSize = 50) => {
  try {
    const params = new URLSearchParams({
      ...(service && { service }),
      ...(date && { date }),
      customer_id: customerId,
      ...(status && status !== 'all' && { status }),
      archived: archived ? 'true' : 'false',
      page: page,
      page_size: pageSize
    });
    const url = `${WORKORDER_BASE_URL}list/?${params.toString()}`;
    const response = await fetch(url, {
      headers: authHeader()
    });
    const workOrders = await response.json();
    return workOrders;
  } catch (error) {
    console.error('Error fetching work orders by customer:', error);
    throw error;
  }
};

export const editWorkOrder = async (workOrderId, workOrderData) => {
  try {
      const response = await axios.put(`${WORKORDER_BASE_URL}edit/${workOrderId}/`, workOrderData, { headers: authHeader() });
      return response.data;
  } catch (error) {
      console.error('Error editing work order:', error);
      if (error.response?.status === 401) {
          // Handle authentication error
          localStorage.removeItem('authToken');
          window.location.href = '/login';
      }
      throw error;
  }
}; 

// The work order's QuickBooks invoice lines, for editing in the invoice template
export const fetchWorkOrderInvoice = async (workOrderId) => {
  const response = await axios.get(`${WORKORDER_BASE_URL}invoice/${workOrderId}/`, { headers: authHeader() });
  return response.data;
};

// The job's QuickBooks invoice lines, for editing in the invoice template
export const fetchJobInvoice = async (jobId) => {
  const response = await axios.get(`${JOBS_BASE_URL}invoice/${jobId}/`, { headers: authHeader() });
  return response.data;
};

// Replace the job's invoice lines in QuickBooks; returns { updated_job, invoice_total, tax }
export const updateJobInvoice = async (jobId, lineItems) => {
  const response = await axios.put(`${JOBS_BASE_URL}invoice/${jobId}/`, { line_items: lineItems }, { headers: authHeader() });
  return response.data;
};

// A job's or work order's QuickBooks invoice as QuickBooks prints it (a PDF Blob)
const fetchInvoicePdf = async (baseUrl, id) => {
  try {
    const response = await axios.get(`${baseUrl}invoice/${id}/pdf/`, { headers: authHeader(), responseType: 'blob' });
    return response.data;
  } catch (error) {
    // The error body arrives as a Blob too; turn it back into { error }
    const body = error.response?.data;
    if (body instanceof Blob) {
      try { error.response.data = JSON.parse(await body.text()); } catch (e) { /* not JSON */ }
    }
    throw error;
  }
};

export const fetchWorkOrderInvoicePdf = (workOrderId) => fetchInvoicePdf(WORKORDER_BASE_URL, workOrderId);
export const fetchJobInvoicePdf = (jobId) => fetchInvoicePdf(JOBS_BASE_URL, jobId);

// Who a job's or work order's QuickBooks invoice would be emailed to, and whether it has been:
// { invoice_number, email, cc, bcc, emailed, emailed_at, delivery_error, total, balance, invoice_sent_at,
//   invoice_sent_to, company_cc, company_bcc, copy_to_company, company_email, email_subject, email_message }
export const fetchInvoiceDelivery = async (isJob, id) => {
  const response = await axios.get(`${isJob ? JOBS_BASE_URL : WORKORDER_BASE_URL}invoice/${id}/send/`, { headers: authHeader() });
  return response.data;
};

// Have QuickBooks email the invoice to { email, cc, bcc } (each comma-separated for several; a blank cc or
// bcc clears it on the invoice); returns the same shape as above
export const sendInvoiceEmail = async (isJob, id, { email, cc, bcc }) => {
  const response = await axios.post(`${isJob ? JOBS_BASE_URL : WORKORDER_BASE_URL}invoice/${id}/send/`, { email, cc, bcc }, { headers: authHeader() });
  return response.data;
};

// Replace the invoice's lines in QuickBooks; returns { updated_work_order, invoice_total, tax }
export const updateWorkOrderInvoice = async (workOrderId, lineItems) => {
  const response = await axios.put(`${WORKORDER_BASE_URL}invoice/${workOrderId}/`, { line_items: lineItems }, { headers: authHeader() });
  return response.data;
};

export const fetchWorkOrder = async (workOrderId) => {
  try {
    const response = await axios.get(`${WORKORDER_BASE_URL}edit/${workOrderId}/`, {
      headers: authHeader()
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching work order:', error);
    throw error;
  }
};






export const createWorkOrder = async (workOrderData) => {
  try {
    const response = await axios.post(WORKORDER_BASE_URL, workOrderData, {
      headers: authHeader()
    });
    return response.data;
  } catch (error) {
    console.error('Error creating work order:', error);
    if (error.response?.status === 401) {
      // Handle authentication error
      localStorage.removeItem('authToken');
      window.location.href = '/login';
    }
    throw error;
  }
};

// Existing customers matching a name, phone or email — checked before creating a
// customer so staff can pick the existing one instead of making a duplicate.
export const checkDuplicateCustomers = async ({ name = '', phone = '', email = '' }) => {
  const params = new URLSearchParams({ name, phone, email });
  const response = await axios.get(`${CUST_BASE_URL}duplicates/?${params.toString()}`, {
    headers: authHeader()
  });
  return response.data.results || [];
};

// Admin Settings: groups of customers sharing a phone, email or name (read-only).
export const fetchDuplicateReport = async () => {
  const response = await axios.get(`${CUST_BASE_URL}duplicate-report/`, {
    headers: authHeader()
  });
  return response.data;
};

export const dismissDuplicateGroup = async (group) => {
  await axios.post(`${CUST_BASE_URL}duplicate-report/dismiss/`, {
    key: group.key,
    customer_ids: group.customer_ids,
  }, {
    headers: authHeader()
  });
};

// What merging these customers would move, with a fingerprint the merge must send back.
export const fetchMergeReview = async (customerIds) => {
  const response = await axios.post(`${CUST_BASE_URL}merge/review/`, {
    customer_ids: customerIds,
  }, {
    headers: authHeader()
  });
  return response.data;
};

// Permanently merges mergeIds into keepId. A 409 means the customers changed since
// the review; its response carries a fresh review.
export const mergeCustomers = async ({ keepId, mergeIds, fields, fingerprint, confirmName }) => {
  const response = await axios.post(`${CUST_BASE_URL}merge/`, {
    keep_id: keepId,
    merge_ids: mergeIds,
    fields,
    fingerprint,
    confirm_name: confirmName,
  }, {
    headers: authHeader()
  });
  return response.data;
};

export const createCustomer = async (customerData) => {
  try {
    const response = await axios.post(`${CUST_BASE_URL}create/`, customerData, {
      headers: authHeader()
    });
    return response.data;
  } catch (error) {
    console.error('Error creating customer:', error);
    throw error;
  }
};



//export const fetchWorkOrders = async () => {
 // try {
 //   const response = await axios.get(`${API_BASE_URL}list/`);
 //   return response.data;
 // } catch (error) {
 //   console.error('Error fetching work orders:', error);
  //  return null;
  //}
//};


export const fetchUsers = async (search, page = 1, pageSize = 50) => {
  const response = await axios.get(CUST_BASE_URL, {
    params: { 
      search,
      page,
      page_size: pageSize
    },
    headers: authHeader()
  });
  return response.data;
};




export const fetchWorkOrderHistory = async (workOrderId) => {
  try {
    const response = await axios.get(`${WORKORDER_BASE_URL}history/${workOrderId}/`, {
      headers: authHeader(), // Include authentication headers if needed
    });
    console.log('Raw API response:', response.data); // Debug log
    return response.data;
  } catch (error) {
    console.error('Error fetching work order history:', error);
    throw error;
  }
};


export const fetchDailyReports = async (type) => {
    try {
        const { start, end } = getDateRange('daily');
        const baseUrl = type === 'retail' ? WORKORDER_BASE_URL : JOBS_BASE_URL;
        const response = await axios.get(`${baseUrl}reports/daily/`, {
            params: {
                start,
                end
            },
            headers: authHeader()
        });
        return response.data;
    } catch (error) {
        console.error('Error fetching daily reports:', error);
        return [];
    }
};

export const fetchWeeklyReports = async (type) => {
    try {
        const { start, end } = getDateRange('weekly');
        const baseUrl = type === 'retail' ? WORKORDER_BASE_URL : JOBS_BASE_URL;
        const response = await axios.get(`${baseUrl}reports/weekly/`, {
            params: {
                start,
                end
            },
            headers: authHeader()
        });
        return response.data;
    } catch (error) {
        console.error('Error fetching weekly reports:', error);
        return [];
    }
};

export const fetchMonthlyReports = async (type) => {
    try {
        const { start, end } = getDateRange('monthly');
        const baseUrl = type === 'retail' ? WORKORDER_BASE_URL : JOBS_BASE_URL;
        const response = await axios.get(`${baseUrl}reports/monthly/`, {
            params: {
                start,
                end
            },
            headers: authHeader()
        });
        return response.data;
    } catch (error) {
        console.error('Error fetching monthly reports:', error);
        return [];
    }
};

export const fetchYearlyReports = async (type) => {
    try {
        const { start, end } = getDateRange('yearly');
        const baseUrl = type === 'retail' ? WORKORDER_BASE_URL : JOBS_BASE_URL;
        const response = await axios.get(`${baseUrl}reports/yearly/`, {
            params: {
                start,
                end
            },
            headers: authHeader()
        });
        return response.data;
    } catch (error) {
        console.error('Error fetching yearly reports:', error);
        return [];
    }
};

// Comprehensive Reports API
export const fetchComprehensiveReports = async (period = 'monthly') => {
    try {
        const response = await axios.get(`${WORKORDER_BASE_URL}reports/comprehensive/`, {
            params: { period },
            headers: authHeader()
        });
        return response.data;
    } catch (error) {
        console.error('Error fetching comprehensive reports:', error);
        throw error;
    }
};

export const fetchComprehensiveJobReports = async (period = 'monthly') => {
    try {
        const response = await axios.get(`${JOBS_BASE_URL}reports/comprehensive/`, {
            params: { period },
            headers: authHeader()
        });
        return response.data;
    } catch (error) {
        console.error('Error fetching comprehensive job reports:', error);
        throw error;
    }
};

// api.js or wherever you manage API calls

/**
 * Fetches jobs from the backend with optional filters and pagination.
 * @param {string} contact - Filter by contact.
 * @param {string} service - Filter by service.
 * @param {string} searchQuery - Search term for filtering jobs.
 * @param {string} status - Filter by status.
 * @param {number} page - Page number for pagination.
 * @param {number} pageSize - Number of items per page.
 * @returns {Promise<Object>} A promise that resolves to a paginated response object.
 */
// contact may be a single contact value or an array of contact values
export const fetchJobs = async (contact, service, searchQuery, status, page = 1, pageSize = 50) => {
  try {
    // Build the query string based on provided filters
    const params = new URLSearchParams({
      ...(service && { service }),
      ...(searchQuery && { search: searchQuery }),
      ...(status && status !== 'all' && { status }),
      page: page,
      page_size: pageSize
    });
    const contacts = (Array.isArray(contact) ? contact : [contact]).filter(Boolean);
    contacts.forEach(value => params.append('contact', value));

    const url = `${JOBS_BASE_URL}list/?${params.toString()}`;
    console.log('Fetching jobs with URL:', url);
    console.log('Filter parameters:', { contact, service, searchQuery, status, page, pageSize });

    // Add timeout to prevent hanging
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...authHeader()
      },
      signal: controller.signal
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      const errorText = await response.text().catch(() => 'Unable to read error response');
      throw new Error(`Network response was not ok: ${response.status} ${response.statusText}. ${errorText}`);
    }

    const jobs = await response.json();
    console.log('Received jobs:', jobs);
    return jobs;
  } catch (error) {
    console.error('Failed to fetch jobs:', error);
    // Re-throw with more context
    if (error.name === 'AbortError') {
      throw new Error('fetchJobs request timed out after 30 seconds');
    }
    throw error;
  }
};

export const fetchJob = async (jobId) => {
  try {
    const response = await axios.get(`${JOBS_BASE_URL}${jobId}/`, {
      headers: authHeader()
    });
    
    // Ensure the picture URL is absolute
    const responseData = { ...response.data };
    if (responseData.picture && typeof responseData.picture === 'string' && !responseData.picture.startsWith('http')) {
      responseData.picture = `${getCurrentConfig().baseUrl}${responseData.picture}`;
    }
    
    return responseData;
  } catch (error) {
    console.error('API Error:', {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status
    });
    throw error;
  }
};

export const editJob = async (jobId, jobData) => {
  try {
    // If jobData is FormData, it's already set up for multipart/form-data
    const isFormData = jobData instanceof FormData;

    // If there's a new picture, upload it directly to S3 and send only the key.
    // Multipart file uploads to this endpoint arrive without the file on mobile browsers.
    if (isFormData && jobData.get('picture') instanceof File) {
      const pictureFile = jobData.get('picture');
      try {
        const fileExtension = pictureFile.name.split('.').pop().toLowerCase();
        const { presigned_url, s3_key, content_type } = await getPresignedUrl('job_image', fileExtension, pictureFile.type);
        await uploadFileToS3(presigned_url, pictureFile, content_type);
        jobData.delete('picture');
        jobData.append('picture_s3_key', s3_key);
      } catch (error) {
        console.error('Error uploading picture to S3, using legacy upload:', error);
        // Continue with legacy upload if S3 fails
      }
    }

    const response = await axios.put(
      `${JOBS_BASE_URL}edit/${jobId}/`,
      jobData,
      {
        headers: {
          ...authHeader(),
          ...(isFormData ? {} : { 'Content-Type': 'application/json' })
        },
        timeout: 120000 // 2 minutes - allows large images to upload
      }
    );
    
    // Ensure the picture URL is absolute
    const responseData = { ...response.data };
    if (responseData.picture && typeof responseData.picture === 'string' && !responseData.picture.startsWith('http')) {
      responseData.picture = `${getCurrentConfig().baseUrl}${responseData.picture}`;
    }
    
    return responseData;
  } catch (error) {
    console.error('API Error:', {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status
    });
    throw error;
  }
};

export const createJob = async (jobData) => {
  try {
    // Check if jobData is FormData (legacy) or regular object (with S3 key)
    const isFormData = jobData instanceof FormData;
    
    // If it's FormData and has a picture file, try direct S3 upload
    if (isFormData && jobData.has('picture')) {
      const pictureFile = jobData.get('picture');
      if (pictureFile instanceof File) {
        try {
          // Get file extension
          const fileExtension = pictureFile.name.split('.').pop().toLowerCase();
          
          // Get presigned URL with content type
          const { presigned_url, s3_key, url, content_type } = await getPresignedUrl('job_image', fileExtension, pictureFile.type);
          
          // Upload directly to S3 with matching content type
          await uploadFileToS3(presigned_url, pictureFile, content_type);
          
          // Remove picture from FormData and add S3 key
          jobData.delete('picture');
          jobData.append('picture_s3_key', s3_key);
        } catch (error) {
          console.error('Error uploading picture to S3, using legacy upload:', error);
          // Continue with legacy upload if S3 fails
        }
      }
    }
    
    const response = await axios.post(JOBS_BASE_URL, jobData, {
      headers: {
        ...authHeader(),
        'Content-Type': isFormData ? 'multipart/form-data' : 'application/json'
      },
      timeout: 120000 // 2 minutes - allows large images to upload
    });
    
    // Ensure the picture URL is absolute
    if (response.data.picture && !response.data.picture.startsWith('http')) {
      response.data.picture = `${getCurrentConfig().baseUrl}${response.data.picture}`;
    }
    
    return response.data;
  } catch (error) {
    console.error('Error creating job:', error);
    throw error;
  }
};

export const fetchCustomer = async (customerId) => {
  try {
    const response = await axios.get(`${CUST_BASE_URL}detail/id/${customerId}/`, {
      headers: authHeader()
    });
    return response.data;
  } catch (error) {
    // A customer deleted by a merge: open the customer it was merged into instead.
    const mergedInto = error.response?.data?.merged_into;
    if (mergedInto && String(mergedInto) !== String(customerId)) {
      return fetchCustomer(mergedInto);
    }
    console.error('Error fetching customer:', error);
    throw error;
  }
};

export const updateCustomer = async (customerData) => {
  try {
    const response = await axios.put(`${CUST_BASE_URL}update/${customerData.id}/`, customerData, {
      headers: authHeader()
    });
    return response.data;
  } catch (error) {
    console.error('Error updating customer:', error);
    throw error;
  }
};

// Update only the given fields, e.g. { email } — leaves the rest of the customer as is.
export const patchCustomer = async (customerId, fields) => {
  const response = await axios.patch(`${CUST_BASE_URL}update/${customerId}/`, fields, {
    headers: authHeader()
  });
  return response.data;
};

export const fetchJobsByCustomer = async (customerId, page = 1, pageSize = 50) => {
  try {
    const params = new URLSearchParams({
      page: page,
      page_size: pageSize
    });

    const response = await fetch(`${JOBS_BASE_URL}list/customer-id/${customerId}/?${params.toString()}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...authHeader()
      }
    });

    if (!response.ok) {
      throw new Error('Network response was not ok');
    }

    const jobs = await response.json();
    return jobs;
  } catch (error) {
    console.error('Failed to fetch jobs by customer:', error);
    throw error;
  }
};

export const updateUserInitials = async (initials) => {
  const token = localStorage.getItem('authToken');
  const response = await axios.put(
    `${USER_API_URL}update_initials/`,
    { initials },
    {
      headers: {
        'Authorization': `Token ${token}`,
        'Content-Type': 'application/json',
      },
    }
  );
  return response.data;
};

// Pass only the fields to change: { default_contact, filter_jobs_by_default_contact }
export const updateUserDefaultContact = async (settings) => {
  const token = localStorage.getItem('authToken');
  const response = await axios.put(
    `${USER_API_URL}update_default_contact/`,
    settings,
    {
      headers: {
        'Authorization': `Token ${token}`,
        'Content-Type': 'application/json',
      },
    }
  );
  return response.data;
};

export const archiveJob = async (jobId) => {
  try {
    const response = await axios.patch(`${JOBS_BASE_URL}archive/${jobId}/`, {}, {
      headers: authHeader()
    });
    return response.data;
  } catch (error) {
    console.error('Error archiving job:', error);
    throw error;
  }
};

export const fetchArchivedJobs = async (contact, service, searchQuery, status, page = 1, pageSize = 50) => {
  try {
    // Build the query string based on provided filters
    const params = new URLSearchParams({
      ...(contact && { contact }),
      ...(service && { service }),
      ...(searchQuery && { search: searchQuery }),
      ...(status && status !== 'all' && { status }),
      archived: true,
      page: page,
      page_size: pageSize
    });

    const url = `${JOBS_BASE_URL}list/?${params.toString()}`;
    console.log('Fetching archived jobs with URL:', url);

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...authHeader()
      }
    });

    if (!response.ok) {
      throw new Error('Network response was not ok');
    }

    const jobs = await response.json();
    console.log('Received archived jobs:', jobs);
    return jobs;
  } catch (error) {
    console.error('Failed to fetch archived jobs:', error);
    throw error;
  }
};

export const unarchiveJob = async (jobId) => {
  try {
    const response = await axios.patch(`${JOBS_BASE_URL}archive/${jobId}/`, { is_archived: false }, {
      headers: authHeader()
    });
    return response.data;
  } catch (error) {
    console.error('Error unarchiving job:', error);
    throw error;
  }
};


// Helper function to format date for API requests in Eastern Time
const formatDate = (date) => {
    return date.toLocaleDateString('en-CA', { 
        timeZone: 'America/New_York',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
    });
};

// Helper function to get date range for different time frames in Eastern Time
const getDateRange = (timeFrame) => {
    const end = new Date();
    const start = new Date();
    
    switch (timeFrame) {
        case 'daily':
            start.setDate(end.getDate() - 30); // Last 30 days
            break;
        case 'weekly':
            start.setDate(end.getDate() - 90); // Last 90 days
            break;
        case 'monthly':
            start.setMonth(end.getMonth() - 12); // Last 12 months
            break;
        case 'yearly':
            start.setFullYear(end.getFullYear() - 5); // Last 5 years
            break;
        default:
            start.setDate(end.getDate() - 30);
    }
    
    return {
        start: formatDate(start),
        end: formatDate(end)
    };
};

export const fetchJobHistory = async (jobId) => {
    try {
        const response = await axios.get(`${JOBS_BASE_URL}history/${jobId}/`, { headers: authHeader() });
        console.log('Raw API response:', response.data); // Debug log
        return response.data;
    } catch (error) {
        console.error('Error fetching job history:', error);
        throw error;
    }
};

// Job Invoice API functions
export const getJobInvoiceTemplate = async (jobId) => {
    try {
        const response = await axios.get(`${JOBS_BASE_URL}invoice-template/${jobId}/`, {
            headers: authHeader()
        });
        return response.data;
    } catch (error) {
        console.error('Error fetching job invoice template:', error);
        throw error;
    }
};

export const createJobInvoice = async (jobId, invoiceData) => {
    try {
        const response = await axios.post(`${JOBS_BASE_URL}create-invoice/${jobId}/`, {
            invoice_data: invoiceData
        }, {
            headers: authHeader()
        });
        return response.data;
    } catch (error) {
        console.error('Error creating job invoice:', error);
        throw error;
    }
};

// Job Photo API functions
export const getJobPhotos = async (jobId) => {
    try {
        const response = await axios.get(`${JOBS_BASE_URL}${jobId}/photos/`, {
            headers: authHeader()
        });
        return response.data;
    } catch (error) {
        console.error('Error fetching job photos:', error);
        throw error;
    }
};

// Get presigned URL for direct S3 upload
export const getPresignedUrl = async (fileType, fileExtension, contentType) => {
    try {
        const response = await axios.post(
            `${JOBS_BASE_URL}photos/presigned-url/`,
            {
                file_type: fileType, // 'job_image' or 'job_photo'
                file_extension: fileExtension,
                content_type: contentType // Pass the actual file content type
            },
            {
                headers: authHeader()
            }
        );
        return response.data;
    } catch (error) {
        console.error('Error getting presigned URL:', error);
        throw error;
    }
};

// Upload file directly to S3 using presigned URL
export const uploadFileToS3 = async (presignedUrl, file, contentType) => {
    try {
        // Use the content type from the presigned URL response (must match exactly)
        const response = await axios.put(presignedUrl, file, {
            headers: {
                'Content-Type': contentType || file.type || 'application/octet-stream'
            },
            onUploadProgress: (progressEvent) => {
                const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                console.log(`Upload progress: ${percentCompleted}%`);
            }
        });
        return response;
    } catch (error) {
        console.error('Error uploading file to S3:', error);
        throw error;
    }
};

export const uploadJobPhotos = async (jobId, files) => {
    try {
        // Try direct S3 upload first
        const s3Keys = [];
        
        for (const file of files) {
            try {
                // Get file extension
                const fileExtension = file.name.split('.').pop().toLowerCase();
                
                // Get presigned URL with content type
                const { presigned_url, s3_key, url, content_type } = await getPresignedUrl('job_photo', fileExtension, file.type);
                
                // Upload directly to S3 with matching content type
                await uploadFileToS3(presigned_url, file, content_type);
                
                s3Keys.push(s3_key);
            } catch (error) {
                console.error('Error uploading file to S3, falling back to legacy upload:', error);
                // Fall back to legacy upload if S3 fails
                const formData = new FormData();
                for (let i = 0; i < files.length; i++) {
                    formData.append('images', files[i]);
                }
                
                const response = await axios.post(`${JOBS_BASE_URL}${jobId}/photos/upload/`, formData, {
                    headers: {
                        ...authHeader(),
                        'Content-Type': 'multipart/form-data'
                    }
                });
                return response.data;
            }
        }
        
        // If all files uploaded to S3 successfully, notify backend
        if (s3Keys.length > 0) {
            const response = await axios.post(
                `${JOBS_BASE_URL}${jobId}/photos/upload/`,
                { s3_keys: s3Keys },
                {
                    headers: {
                        ...authHeader(),
                        'Content-Type': 'application/json'
                    }
                }
            );
            return response.data;
        }
        
        throw new Error('No files uploaded');
    } catch (error) {
        console.error('Error uploading job photos:', error);
        throw error;
    }
};

export const deleteJobPhoto = async (photoId) => {
    try {
        const response = await axios.delete(`${JOBS_BASE_URL}photos/${photoId}/delete/`, {
            headers: authHeader()
        });
        return response.data;
    } catch (error) {
        console.error('Error deleting job photo:', error);
        throw error;
    }
};

export const reorderJobPhotos = async (jobId, photoOrders) => {
    try {
        const response = await axios.patch(`${JOBS_BASE_URL}${jobId}/photos/reorder/`, {
            photo_orders: photoOrders
        }, {
            headers: authHeader()
        });
        return response.data;
    } catch (error) {
        console.error('Error reordering job photos:', error);
        throw error;
    }
};

// Quotes API functions
export const getQuotes = async (showArchived = false, page = 1, pageSize = 50, customerId = null) => {
  try {
    const params = new URLSearchParams({
      ...(showArchived !== null && { archived: showArchived }),
      ...(customerId && { customer_id: customerId }),
      page: page,
      page_size: pageSize
    });
    const url = `${QUOTES_BASE_URL}quotes/?${params.toString()}`;
    const response = await fetch(url, {
      headers: authHeader()
    });
    const quotes = await response.json();
    return quotes;
  } catch (error) {
    console.error('Error fetching quotes:', error);
    throw error;
  }
};

export const getQuote = async (quoteId) => {
  try {
    const response = await axios.get(`${QUOTES_BASE_URL}quotes/${quoteId}/`, {
      headers: authHeader()
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching quote:', error);
    throw error;
  }
};

export const createQuote = async (quoteData) => {
  try {
    const response = await axios.post(`${QUOTES_BASE_URL}quotes/`, quoteData, {
      headers: authHeader()
    });
    return response.data;
  } catch (error) {
    console.error('Error creating quote:', error);
    throw error;
  }
};

export const updateQuote = async (quoteId, quoteData) => {
  try {
    const response = await axios.put(`${QUOTES_BASE_URL}quotes/${quoteId}/`, quoteData, {
      headers: authHeader()
    });
    return response.data;
  } catch (error) {
    console.error('Error updating quote:', error);
    throw error;
  }
};

export const deleteQuote = async (quoteId) => {
  try {
    const response = await axios.delete(`${QUOTES_BASE_URL}quotes/${quoteId}/`, {
      headers: authHeader()
    });
    return response.data;
  } catch (error) {
    console.error('Error deleting quote:', error);
    throw error;
  }
};

export const archiveQuote = async (quoteId) => {
  try {
    const response = await axios.post(`${QUOTES_BASE_URL}quotes/${quoteId}/archive/`, {}, {
      headers: authHeader()
    });
    return response.data;
  } catch (error) {
    console.error('Error archiving quote:', error);
    throw error;
  }
};

export const unarchiveQuote = async (quoteId) => {
  try {
    const response = await axios.post(`${QUOTES_BASE_URL}quotes/${quoteId}/unarchive/`, {}, {
      headers: authHeader()
    });
    return response.data;
  } catch (error) {
    console.error('Error unarchiving quote:', error);
    throw error;
  }
};

export const getCustomers = async () => {
  try {
    const response = await axios.get(`${CUST_BASE_URL}`, {
      headers: authHeader()
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching customers:', error);
    throw error;
  }
};

// User management API functions
export const createUser = async (userData) => {
  try {
    const response = await axios.post(`${USER_API_URL}create_user/`, userData, {
      headers: authHeader()
    });
    return response.data;
  } catch (error) {
    console.error('Error creating user:', error);
    throw error;
  }
};

export const fetchAllUsers = async () => {
  try {
    const response = await axios.get(`${USER_API_URL}users/`, {
      headers: authHeader()
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching users:', error);
    throw error;
  }
};

export const fetchUser = async (userId) => {
  try {
    const response = await axios.get(`${USER_API_URL}users/${userId}/`, {
      headers: authHeader()
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching user:', error);
    throw error;
  }
};

export const updateUser = async (userId, userData) => {
  try {
    const response = await axios.put(`${USER_API_URL}users/${userId}/`, userData, {
      headers: authHeader()
    });
    return response.data;
  } catch (error) {
    console.error('Error updating user:', error);
    throw error;
  }
};

export const deleteUser = async (userId) => {
  try {
    const response = await axios.delete(`${USER_API_URL}users/${userId}/delete/`, {
      headers: authHeader()
    });
    return response.data;
  } catch (error) {
    console.error('Error deleting user:', error);
    throw error;
  }
};

// Choice Management API functions
export const fetchChoices = async (choiceType = null) => {
  try {
    const params = choiceType ? { type: choiceType } : {};
    const response = await axios.get(`${JOBS_BASE_URL}choices/`, {
      params,
      headers: authHeader()
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching choices:', error);
    throw error;
  }
};

export const createChoice = async (choiceData) => {
  try {
    const response = await axios.post(`${JOBS_BASE_URL}choices/create/`, choiceData, {
      headers: authHeader()
    });
    return response.data;
  } catch (error) {
    console.error('Error creating choice:', error);
    throw error;
  }
};

export const updateChoice = async (choiceId, choiceData) => {
  try {
    const response = await axios.put(`${JOBS_BASE_URL}choices/${choiceId}/`, choiceData, {
      headers: authHeader()
    });
    return response.data;
  } catch (error) {
    console.error('Error updating choice:', error);
    throw error;
  }
};

export const deleteChoice = async (choiceId) => {
  try {
    const response = await axios.delete(`${JOBS_BASE_URL}choices/${choiceId}/delete/`, {
      headers: authHeader()
    });
    return response.data;
  } catch (error) {
    console.error('Error deleting choice:', error);
    throw error;
  }
};

// Company Settings API functions
export const getCompanySettings = async () => {
  try {
    const response = await axios.get(`${WORKORDER_BASE_URL}company-settings/`, {
      headers: authHeader()
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching company settings:', error);
    throw error;
  }
};

export const updateCompanySettings = async (settings) => {
  try {
    const response = await axios.post(`${WORKORDER_BASE_URL}update-company-settings/`, settings, {
      headers: authHeader()
    });
    return response.data;
  } catch (error) {
    console.error('Error updating company settings:', error);
    throw error;
  }
};

// QuickBooks Data API functions
export const fetchQuickBooksCustomers = async () => {
  try {
    const response = await axios.get(`${QUICKBOOKS_BASE_URL}customers/`, {
      headers: authHeader()
    });
    return response.data.customers;
  } catch (error) {
    console.error('Error fetching QuickBooks customers:', error);
    throw error;
  }
};

export const fetchQuickBooksItems = async () => {
  try {
    const response = await axios.get(`${QUICKBOOKS_BASE_URL}items/`, {
      headers: authHeader()
    });
    return response.data.items;
  } catch (error) {
    console.error('Error fetching QuickBooks items:', error);
    throw error;
  }
};

// Which QuickBooks item each app service (and tire disposal) pre-selects on invoices
export const fetchQuickBooksServiceMappings = async () => {
  const response = await axios.get(`${QUICKBOOKS_BASE_URL}service-mappings/`, {
    headers: authHeader()
  });
  return response.data;
};

// mappings: [{ key, qb_item_id, qb_item_name }] — an empty qb_item_id removes the mapping
export const saveQuickBooksServiceMappings = async (mappings) => {
  const response = await axios.put(`${QUICKBOOKS_BASE_URL}service-mappings/`, { mappings }, {
    headers: authHeader()
  });
  return response.data;
};

export const fetchQuickBooksCompanyInfo = async () => {
  try {
    const response = await axios.get(`${QUICKBOOKS_BASE_URL}company-info/`, {
      headers: authHeader()
    });
    return response.data.company_info;
  } catch (error) {
    console.error('Error fetching QuickBooks company info:', error);
    throw error;
  }
};

export const fetchQuickBooksCustomFields = async () => {
  try {
    const response = await axios.get(`${QUICKBOOKS_BASE_URL}custom-fields/`, {
      headers: authHeader()
    });
    return response.data.custom_fields;
  } catch (error) {
    console.error('Error fetching QuickBooks custom fields:', error);
    throw error;
  }
};

// Create axios instance for API calls with dynamic base URL
const api = axios.create({
  baseURL: getCurrentConfig().baseUrl + '/',
  headers: {
    'Content-Type': 'application/json',
  }
});

// Add request interceptor to include auth token
api.interceptors.request.use(
  (config) => {
    const token = getAuthToken();
    if (token) {
      config.headers.Authorization = `Token ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Notification API functions
export const fetchNotifications = async (params = {}, page = 1, pageSize = 50) => {
  try {
    const response = await axios.get(`${NOTIFICATIONS_BASE_URL}`, {
      headers: authHeader(),
      params: {
        ...params,
        page: page,
        page_size: pageSize
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching notifications:', error);
    throw error;
  }
};

export const fetchNotificationSummary = async () => {
  try {
    const response = await axios.get(`${NOTIFICATIONS_BASE_URL}summary/`, {
      headers: authHeader()
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching notification summary:', error);
    throw error;
  }
};

export const markNotificationAsRead = async (notificationId) => {
  try {
    const response = await axios.post(`${NOTIFICATIONS_BASE_URL}${notificationId}/mark-read/`, {}, {
      headers: authHeader()
    });
    return response.data;
  } catch (error) {
    console.error('Error marking notification as read:', error);
    throw error;
  }
};

export const markAllNotificationsAsRead = async () => {
  try {
    const response = await axios.post(`${NOTIFICATIONS_BASE_URL}mark-all-read/`, {}, {
      headers: authHeader()
    });
    return response.data;
  } catch (error) {
    console.error('Error marking all notifications as read:', error);
    throw error;
  }
};

export const fetchSubscriptions = async () => {
  try {
    const response = await axios.get(`${NOTIFICATIONS_BASE_URL}subscriptions/`, {
      headers: authHeader()
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching subscriptions:', error);
    throw error;
  }
};

export const createSubscription = async (subscriptionData) => {
  try {
    const response = await axios.post(`${NOTIFICATIONS_BASE_URL}subscriptions/create/`, subscriptionData, {
      headers: authHeader()
    });
    return response.data;
  } catch (error) {
    console.error('Error creating subscription:', error);
    throw error;
  }
};

export const deleteSubscription = async (subscriptionId) => {
  try {
    const response = await axios.delete(`${NOTIFICATIONS_BASE_URL}subscriptions/${subscriptionId}/`, {
      headers: authHeader()
    });
    return response.data;
  } catch (error) {
    console.error('Error deleting subscription:', error);
    throw error;
  }
};

export const fetchAvailableContacts = async () => {
  try {
    const response = await axios.get(`${NOTIFICATIONS_BASE_URL}available/contacts/`, {
      headers: authHeader()
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching available contacts:', error);
    throw error;
  }
};

export const fetchAvailableServices = async () => {
  try {
    const response = await axios.get(`${NOTIFICATIONS_BASE_URL}available/services/`, {
      headers: authHeader()
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching available services:', error);
    throw error;
  }
};

export const fetchAvailableStatuses = async () => {
  try {
    const response = await axios.get(`${NOTIFICATIONS_BASE_URL}available/statuses/`, {
      headers: authHeader()
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching available statuses:', error);
    throw error;
  }
};

export const fetchAvailableWorkOrderServices = async () => {
  try {
    const response = await axios.get(`${NOTIFICATIONS_BASE_URL}available/workorder-services/`, {
      headers: authHeader()
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching available work order services:', error);
    throw error;
  }
};

export const fetchAvailableWorkOrderStatuses = async () => {
  try {
    const response = await axios.get(`${NOTIFICATIONS_BASE_URL}available/workorder-statuses/`, {
      headers: authHeader()
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching available work order statuses:', error);
    throw error;
  }
};

// PWA Device Registration functions
export const registerPWADevice = async (deviceData) => {
  try {
    const response = await axios.post(`${NOTIFICATIONS_BASE_URL}register-device/`, deviceData, {
      headers: authHeader()
    });
    return response.data;
  } catch (error) {
    console.error('Error registering PWA device:', error);
    throw error;
  }
};

export const unregisterPWADevice = async () => {
  try {
    const response = await axios.delete(`${NOTIFICATIONS_BASE_URL}register-device/`, {
      headers: authHeader()
    });
    return response.data;
  } catch (error) {
    console.error('Error unregistering PWA device:', error);
    throw error;
  }
};

export const getUnreadCount = async () => {
  try {
    const response = await axios.get(`${NOTIFICATIONS_BASE_URL}unread-count/`, {
      headers: authHeader()
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching unread count:', error);
    throw error;
  }
};

export const getNotificationHistory = async (params = {}) => {
  try {
    const response = await axios.get(`${NOTIFICATIONS_BASE_URL}history/`, {
      headers: authHeader(),
      params
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching notification history:', error);
    throw error;
  }
};

// Retail Customer Settings API functions
export const fetchRetailCustomerSetting = async () => {
  try {
    const response = await axios.get(`${WORKORDER_BASE_URL}retail-customer-setting/`, {
      headers: authHeader()
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching retail customer setting:', error);
    throw error;
  }
};

export const updateRetailCustomerSetting = async (retailCustomerName) => {
  try {
    const response = await axios.post(`${WORKORDER_BASE_URL}update-retail-customer-setting/`, {
      retail_customer_name: retailCustomerName
    }, {
      headers: authHeader()
    });
    return response.data;
  } catch (error) {
    console.error('Error updating retail customer setting:', error);
    throw error;
  }
};

export const verifyReportsPassword = async (password) => {
  try {
    const response = await axios.post(`${WORKORDER_BASE_URL}verify-reports-password/`, {
      password: password
    }, {
      headers: authHeader()
    });
    return response.data;
  } catch (error) {
    console.error('Error verifying reports password:', error);
    throw error;
  }
};

// Wheel Library API functions

export const fetchWheels = async (search = '', page = 1, pageSize = 50) => {
  try {
    const params = new URLSearchParams({
      ...(search && { search }),
      page,
      page_size: pageSize,
    });
    const response = await fetch(`${WHEELS_BASE_URL}list/?${params.toString()}`, {
      headers: authHeader(),
    });
    return await response.json();
  } catch (error) {
    console.error('Error fetching wheels:', error);
    throw error;
  }
};

export const createWheel = async (wheelData) => {
  try {
    const response = await axios.post(WHEELS_BASE_URL, wheelData, {
      headers: authHeader(),
    });
    return response.data;
  } catch (error) {
    console.error('Error creating wheel:', error);
    throw error;
  }
};

export const editWheel = async (wheelId, wheelData) => {
  try {
    const response = await axios.patch(`${WHEELS_BASE_URL}${wheelId}/`, wheelData, {
      headers: authHeader(),
    });
    return response.data;
  } catch (error) {
    console.error('Error editing wheel:', error);
    throw error;
  }
};

export const deleteWheel = async (wheelId) => {
  try {
    const response = await axios.delete(`${WHEELS_BASE_URL}${wheelId}/delete/`, {
      headers: authHeader(),
    });
    return response.data;
  } catch (error) {
    console.error('Error deleting wheel:', error);
    throw error;
  }
};

export const uploadWheelImages = async (wheelId, files) => {
  try {
    const s3Keys = [];

    for (const file of files) {
      try {
        const fileExtension = file.name.split('.').pop().toLowerCase();
        const presignedResponse = await axios.post(
          `${WHEELS_BASE_URL}images/presigned-url/`,
          { file_extension: fileExtension, content_type: file.type },
          { headers: authHeader() }
        );
        const { presigned_url, s3_key, content_type } = presignedResponse.data;
        await axios.put(presigned_url, file, { headers: { 'Content-Type': content_type } });
        s3Keys.push(s3_key);
      } catch (s3Error) {
        console.warn('S3 upload failed, falling back to direct upload:', s3Error);
        // Fall through to direct upload below
      }
    }

    if (s3Keys.length > 0) {
      const response = await axios.post(
        `${WHEELS_BASE_URL}${wheelId}/images/upload/`,
        { s3_keys: s3Keys },
        { headers: authHeader() }
      );
      return response.data;
    }

    // Direct upload fallback
    const formData = new FormData();
    files.forEach((f) => formData.append('images', f));
    const response = await axios.post(
      `${WHEELS_BASE_URL}${wheelId}/images/upload/`,
      formData,
      { headers: { ...authHeader(), 'Content-Type': 'multipart/form-data' } }
    );
    return response.data;
  } catch (error) {
    console.error('Error uploading wheel images:', error);
    throw error;
  }
};

export const deleteWheelImage = async (imageId) => {
  try {
    const response = await axios.delete(`${WHEELS_BASE_URL}images/${imageId}/delete/`, {
      headers: authHeader(),
    });
    return response.data;
  } catch (error) {
    console.error('Error deleting wheel image:', error);
    throw error;
  }
};

export const setPrimaryWheelImage = async (imageId) => {
  try {
    const response = await axios.patch(
      `${WHEELS_BASE_URL}images/${imageId}/set-primary/`,
      {},
      { headers: authHeader() }
    );
    return response.data;
  } catch (error) {
    console.error('Error setting primary wheel image:', error);
    throw error;
  }
};

export const searchWheelsByImage = async (imageFile, topK = 10) => {
  try {
    const formData = new FormData();
    formData.append('image', imageFile);
    const response = await axios.post(
      `${WHEELS_BASE_URL}images/search/?top_k=${topK}`,
      formData,
      { headers: { ...authHeader(), 'Content-Type': 'multipart/form-data' } }
    );
    return response.data;
  } catch (error) {
    console.error('Error searching wheels by image:', error);
    throw error;
  }
};

// Scheduling API functions

export const fetchAppointments = async (dateFrom, dateTo, statusFilter = null, page = 1, pageSize = 100) => {
  try {
    const params = new URLSearchParams({
      ...(dateFrom && { date_from: dateFrom }),
      ...(dateTo && { date_to: dateTo }),
      ...(statusFilter && { status: statusFilter }),
      page,
      page_size: pageSize,
    });
    const response = await fetch(`${SCHEDULING_BASE_URL}appointments/?${params.toString()}`, {
      headers: authHeader(),
    });
    return await response.json();
  } catch (error) {
    console.error('Error fetching appointments:', error);
    throw error;
  }
};

export const fetchAppointmentsByCustomer = async (customerId, page = 1, pageSize = 200) => {
  try {
    const params = new URLSearchParams({ customer: customerId, page, page_size: pageSize });
    const response = await fetch(`${SCHEDULING_BASE_URL}appointments/?${params.toString()}`, {
      headers: authHeader(),
    });
    return await response.json();
  } catch (error) {
    console.error('Error fetching appointments for customer:', error);
    throw error;
  }
};

export const fetchAppointment = async (appointmentId) => {
  try {
    const response = await axios.get(`${SCHEDULING_BASE_URL}appointments/${appointmentId}/`, {
      headers: authHeader(),
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching appointment:', error);
    throw error;
  }
};

export const createAppointment = async (appointmentData) => {
  try {
    const response = await axios.post(`${SCHEDULING_BASE_URL}appointments/`, appointmentData, {
      headers: authHeader(),
    });
    return response.data;
  } catch (error) {
    console.error('Error creating appointment:', error);
    throw error;
  }
};

export const updateAppointment = async (appointmentId, appointmentData) => {
  try {
    const response = await axios.patch(`${SCHEDULING_BASE_URL}appointments/${appointmentId}/`, appointmentData, {
      headers: authHeader(),
    });
    return response.data;
  } catch (error) {
    console.error('Error updating appointment:', error);
    throw error;
  }
};

// sendEmail: whether to email the customer — staff are asked in the appointment modal.
export const cancelAppointment = async (appointmentId, { sendEmail = false } = {}) => {
  try {
    const response = await axios.post(`${SCHEDULING_BASE_URL}appointments/${appointmentId}/cancel/`, { send_email: sendEmail }, {
      headers: authHeader(),
    });
    return response.data;
  } catch (error) {
    console.error('Error cancelling appointment:', error);
    throw error;
  }
};

export const confirmAppointment = async (appointmentId) => {
  try {
    const response = await axios.post(`${SCHEDULING_BASE_URL}appointments/${appointmentId}/confirm/`, {}, {
      headers: authHeader(),
    });
    return response.data;
  } catch (error) {
    console.error('Error confirming appointment:', error);
    throw error;
  }
};

export const deleteAppointment = async (appointmentId) => {
  try {
    const response = await axios.delete(`${SCHEDULING_BASE_URL}appointments/${appointmentId}/`, {
      headers: authHeader(),
    });
    return response.data;
  } catch (error) {
    console.error('Error deleting appointment:', error);
    throw error;
  }
};

// ── Appointment customer emails ──────────────────────────────────────────────

export const fetchAppointmentEmails = async (appointmentId) => {
  try {
    const response = await axios.get(`${SCHEDULING_BASE_URL}appointments/${appointmentId}/emails/`, {
      headers: authHeader(),
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching appointment emails:', error);
    throw error;
  }
};

// kind: 'confirmation' | 'reminder' | 'updated' | 'cancelled'
export const sendAppointmentEmail = async (appointmentId, kind) => {
  try {
    const response = await axios.post(
      `${SCHEDULING_BASE_URL}appointments/${appointmentId}/send-email/`,
      { kind },
      { headers: authHeader() },
    );
    return response.data;
  } catch (error) {
    console.error('Error sending appointment email:', error);
    throw error;
  }
};

// Clears reminder_sent_at so the nightly cron picks the appointment up again.
export const rearmAppointmentReminder = async (appointmentId) => {
  try {
    const response = await axios.post(
      `${SCHEDULING_BASE_URL}appointments/${appointmentId}/rearm-reminder/`,
      {},
      { headers: authHeader() },
    );
    return response.data;
  } catch (error) {
    console.error('Error re-arming appointment reminder:', error);
    throw error;
  }
};

export const fetchAppointmentTypes = async (activeOnly = false) => {
  try {
    const params = activeOnly ? '?is_active=true' : '';
    const response = await axios.get(`${SCHEDULING_BASE_URL}appointment-types/${params}`, {
      headers: authHeader(),
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching appointment types:', error);
    throw error;
  }
};

export const createAppointmentType = async (typeData) => {
  try {
    const response = await axios.post(`${SCHEDULING_BASE_URL}appointment-types/`, typeData, {
      headers: authHeader(),
    });
    return response.data;
  } catch (error) {
    console.error('Error creating appointment type:', error);
    throw error;
  }
};

export const updateAppointmentType = async (typeId, typeData) => {
  try {
    const response = await axios.patch(`${SCHEDULING_BASE_URL}appointment-types/${typeId}/`, typeData, {
      headers: authHeader(),
    });
    return response.data;
  } catch (error) {
    console.error('Error updating appointment type:', error);
    throw error;
  }
};

export const deleteAppointmentType = async (typeId) => {
  try {
    const response = await axios.delete(`${SCHEDULING_BASE_URL}appointment-types/${typeId}/`, {
      headers: authHeader(),
    });
    return response.data;
  } catch (error) {
    console.error('Error deleting appointment type:', error);
    throw error;
  }
};

export const fetchBusinessHours = async () => {
  try {
    const response = await axios.get(`${SCHEDULING_BASE_URL}business-hours/`, {
      headers: authHeader(),
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching business hours:', error);
    throw error;
  }
};

export const updateBusinessHours = async (id, hoursData) => {
  try {
    const response = await axios.patch(`${SCHEDULING_BASE_URL}business-hours/${id}/`, hoursData, {
      headers: authHeader(),
    });
    return response.data;
  } catch (error) {
    console.error('Error updating business hours:', error);
    throw error;
  }
};

export const fetchServiceHours = async (appointmentTypeId) => {
  try {
    const response = await axios.get(`${SCHEDULING_BASE_URL}service-hours/?appointment_type=${appointmentTypeId}`, {
      headers: authHeader(),
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching service hours:', error);
    throw error;
  }
};

export const updateServiceHours = async (id, hoursData) => {
  try {
    const response = await axios.patch(`${SCHEDULING_BASE_URL}service-hours/${id}/`, hoursData, {
      headers: authHeader(),
    });
    return response.data;
  } catch (error) {
    console.error('Error updating service hours:', error);
    throw error;
  }
};

export const fetchAvailabilityExceptions = async (dateFrom, dateTo) => {
  try {
    const params = new URLSearchParams({
      ...(dateFrom && { date_from: dateFrom }),
      ...(dateTo && { date_to: dateTo }),
    });
    const response = await axios.get(`${SCHEDULING_BASE_URL}exceptions/?${params.toString()}`, {
      headers: authHeader(),
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching availability exceptions:', error);
    throw error;
  }
};

export const createAvailabilityException = async (exceptionData) => {
  try {
    const response = await axios.post(`${SCHEDULING_BASE_URL}exceptions/`, exceptionData, {
      headers: authHeader(),
    });
    return response.data;
  } catch (error) {
    console.error('Error creating availability exception:', error);
    throw error;
  }
};

export const deleteAvailabilityException = async (exceptionId) => {
  try {
    const response = await axios.delete(`${SCHEDULING_BASE_URL}exceptions/${exceptionId}/`, {
      headers: authHeader(),
    });
    return response.data;
  } catch (error) {
    console.error('Error deleting availability exception:', error);
    throw error;
  }
};

// "Stop all emails" for one address — staff set it when a customer asks.
export const fetchEmailOptOut = async (email) => {
  try {
    const response = await axios.get(`${SCHEDULING_BASE_URL}email-opt-out/`, {
      headers: authHeader(),
      params: { email },
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching email opt-out:', error);
    throw error;
  }
};

export const createEmailOptOut = async (email, note) => {
  try {
    const response = await axios.post(`${SCHEDULING_BASE_URL}email-opt-out/`, { email, note }, {
      headers: authHeader(),
    });
    return response.data;
  } catch (error) {
    console.error('Error creating email opt-out:', error);
    throw error;
  }
};

export const deleteEmailOptOut = async (email) => {
  try {
    const response = await axios.delete(`${SCHEDULING_BASE_URL}email-opt-out/`, {
      headers: authHeader(),
      params: { email },
    });
    return response.data;
  } catch (error) {
    console.error('Error deleting email opt-out:', error);
    throw error;
  }
};

export const fetchSchedulingSettings = async () => {
  try {
    const response = await axios.get(`${SCHEDULING_BASE_URL}settings/`, {
      headers: authHeader(),
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching scheduling settings:', error);
    throw error;
  }
};

export const updateSchedulingSettings = async (settingsData) => {
  try {
    const response = await axios.put(`${SCHEDULING_BASE_URL}settings/`, settingsData, {
      headers: authHeader(),
    });
    return response.data;
  } catch (error) {
    console.error('Error updating scheduling settings:', error);
    throw error;
  }
};

export const fetchAvailableSlots = async (appointmentTypeId, dateFrom, dateTo) => {
  try {
    const params = new URLSearchParams({
      appointment_type: appointmentTypeId,
      ...(dateFrom && { date_from: dateFrom }),
      ...(dateTo && { date_to: dateTo }),
    });
    const response = await axios.get(`${SCHEDULING_BASE_URL}availability/?${params.toString()}`, {
      headers: authHeader(),
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching available slots:', error);
    throw error;
  }
};

// Export the axios instance and base URLs for direct API calls
export { api, QUICKBOOKS_BASE_URL, WORKORDER_BASE_URL, JOBS_BASE_URL };

// ---------------------------------------------------------------------------
// Sales inbox (crm app) — every call needs a user with the sales permission
// ---------------------------------------------------------------------------

const crmGet = async (path, params) => {
  const response = await axios.get(`${CRM_BASE_URL}${path}`, { headers: authHeader(), params });
  return response.data;
};

const crmSend = async (method, path, data) => {
  const response = await axios({ method, url: `${CRM_BASE_URL}${path}`, data, headers: authHeader() });
  return response.data;
};

export const fetchInquiries = (params) => crmGet('inquiries/', params);
export const fetchInquiry = (id) => crmGet(`inquiries/${id}/`);
export const createInquiry = (data) => crmSend('post', 'inquiries/', data);
export const updateInquiry = (id, data) => crmSend('patch', `inquiries/${id}/`, data);
export const fetchInquiryTimeline = (id) => crmGet(`inquiries/${id}/timeline/`);
export const linkInquiryCustomer = (id, customerId) => crmSend('post', `inquiries/${id}/link-customer/`, { customer: customerId });
export const createInquiryCustomer = (id, data) => crmSend('post', `inquiries/${id}/create-customer/`, data);
export const recordInquiryConversion = (id, data) => crmSend('post', `inquiries/${id}/conversions/`, data);
// files: [{ file, isQuote }]. With files the email goes as a multipart form.
export const sendInquiryEmail = (id, data, files = []) => {
  if (!files.length) return crmSend('post', `inquiries/${id}/send-email/`, data);
  const form = new FormData();
  Object.entries(data).forEach(([key, value]) => {
    if (value !== undefined && value !== null) form.append(key, value);
  });
  files.forEach(({ file, isQuote }, index) => {
    form.append('files', file, file.name);
    if (isQuote) form.append('quote_files', String(index));
  });
  return crmSend('post', `inquiries/${id}/send-email/`, form);
};
export const addInquiryAttachments = (id, files, isQuote) => {
  const form = new FormData();
  files.forEach((file) => form.append('files', file, file.name));
  form.append('is_quote', isQuote ? 'true' : 'false');
  return crmSend('post', `inquiries/${id}/attachments/`, form);
};
export const updateInquiryAttachment = (id, data) => crmSend('patch', `attachments/${id}/`, data);
export const logCrmActivity = (data) => crmSend('post', 'activities/', data);
export const fetchFollowUps = (params) => crmGet('follow-ups/', params);
export const createFollowUp = (data) => crmSend('post', 'follow-ups/', data);
export const updateFollowUp = (id, data) => crmSend('patch', `follow-ups/${id}/`, data);
export const deleteFollowUp = (id) => crmSend('delete', `follow-ups/${id}/`);
export const createCrmContact = (data) => crmSend('post', 'contacts/', data);
export const updateCrmContact = (id, data) => crmSend('patch', `contacts/${id}/`, data);
export const deleteCrmContact = (id) => crmSend('delete', `contacts/${id}/`);
export const fetchCrmSummary = () => crmGet('summary/');
export const fetchSalesUsers = () => crmGet('sales-users/');

// Inquiry photos are private: fetched with the staff token and shown from a blob URL.
export const fetchInquiryAttachmentUrl = async (attachmentId) => {
  const response = await axios.get(`${CRM_BASE_URL}attachments/${attachmentId}/`, {
    headers: authHeader(),
    responseType: 'blob',
  });
  return URL.createObjectURL(response.data);
};

// Inventory (powder coat for now). Quantities change only through weigh / receive.
const inventoryRequest = async (method, path, data, params) => {
  const response = await axios({ method, url: `${INVENTORY_BASE_URL}${path}`, data, params, headers: authHeader() });
  return response.data;
};

export const fetchInventoryItems = (params) => inventoryRequest('get', 'items/', undefined, params);
export const createInventoryItem = (data) => inventoryRequest('post', 'items/', data);
export const updateInventoryItem = (id, data) => inventoryRequest('patch', `items/${id}/`, data);
export const weighInventoryItem = (id, data) => inventoryRequest('post', `items/${id}/weigh/`, data);
export const receiveInventoryItem = (id, data) => inventoryRequest('post', `items/${id}/receive/`, data);
export const fetchInventoryHistory = (id) => inventoryRequest('get', `items/${id}/history/`);
export const fetchInventoryBins = (params) => inventoryRequest('get', 'bins/', undefined, params);
export const createInventoryBin = (data) => inventoryRequest('post', 'bins/', data);
export const updateInventoryBin = (id, data) => inventoryRequest('patch', `bins/${id}/`, data);
export const deleteInventoryBin = (id) => inventoryRequest('delete', `bins/${id}/`);

// Commercial portal (business customers sending in jobs on the website). Setup is
// admin-only; anyone can mark an incoming job received.
const portalRequest = async (method, path, data, params) => {
  const response = await axios({ method, url: `${PORTAL_BASE_URL}staff/${path}`, data, params, headers: authHeader() });
  return response.data;
};

export const fetchPortalOptions = () => portalRequest('get', 'options/');
export const fetchPortalAccounts = () => portalRequest('get', 'accounts/');
export const createPortalAccount = (customerId) => portalRequest('post', 'accounts/', { customer: customerId });
export const fetchPortalAccount = (id) => portalRequest('get', `accounts/${id}/`);
export const updatePortalAccount = (id, data) => portalRequest('patch', `accounts/${id}/`, data);
export const fetchPortalActivity = (id) => portalRequest('get', `accounts/${id}/activity/`);
export const addPortalMember = (accountId, data) => portalRequest('post', `accounts/${accountId}/members/`, data);
export const updatePortalMember = (id, data) => portalRequest('patch', `members/${id}/`, data);
export const removePortalMember = (id) => portalRequest('delete', `members/${id}/`);
export const invitePortalMember = (id) => portalRequest('post', `members/${id}/invite/`);
export const fetchPortalSessions = (personId) => portalRequest('get', `people/${personId}/sessions/`);
export const signOutPortalPerson = (personId) => portalRequest('post', `people/${personId}/sign-out/`);
export const revokePortalSession = (id) => portalRequest('post', `sessions/${id}/revoke/`);
export const receivePortalJob = (jobId) => portalRequest('post', `jobs/${jobId}/receive/`);

export const fetchIncomingJobs = async (contacts = [], search = '') => {
  const params = new URLSearchParams({ incoming: 'true', page_size: 200 });
  if (search) params.append('search', search);
  contacts.filter(Boolean).forEach((value) => params.append('contact', value));
  const response = await axios.get(`${JOBS_BASE_URL}list/?${params.toString()}`, { headers: authHeader() });
  return response.data;
};
